import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { MixerLoader, Table, TableCell, TableHeadCell, TableRow } from '../../Common';
import { ProjectAssigneesWithUserInfoDto } from '../../../commonTypes/project.types';
import { PublicUserDto } from '../../../commonTypes/user.types';
import { useTranslation } from '../../../hooks/useTranslation';
import { formatToMonthDayYear } from '../../../util/date/dateFormater';
import useHttpGetProjectAssigneeAssignmentHistory from '../../../hooks/project/projectApi/useHttpGetProjectAssigneeAssignmentHistory';
import useHttpGetDisplayProfilesByUserSubsIncludingDetached from '../../../hooks/user/userApi/useHttpGetDisplayProfilesByUserSubsIncludingDetached';

type ProjectAssigneeAssignmentHistoryProps = {
    projectId: string;
    projectStartDate?: string;
    assignees: ProjectAssigneesWithUserInfoDto[];
    organizationMembers: PublicUserDto[];
    enabled: boolean;
    refreshKey: number;
};

export const ProjectAssigneeAssignmentHistory = ({
    projectId,
    projectStartDate,
    assignees,
    organizationMembers,
    enabled,
    refreshKey,
}: ProjectAssigneeAssignmentHistoryProps) => {
    const { t } = useTranslation();
    const { rows, isReady } = useHttpGetProjectAssigneeAssignmentHistory(projectId, enabled, projectStartDate, refreshKey);
    const { profilesBySub, exec: fetchDisplayProfiles } = useHttpGetDisplayProfilesByUserSubsIncludingDetached();

    const historyUserSubs = useMemo(() => Array.from(new Set(rows.map(row => row.userSub).filter(Boolean))), [rows]);

    useEffect(() => {
        if (!historyUserSubs.length) return;
        return fetchDisplayProfiles(historyUserSubs);
    }, [historyUserSubs, fetchDisplayProfiles]);

    const nameBySub = new Map<string, string>();
    const organizationMemberSubs = new Set(organizationMembers.map(member => member.userSub));

    Object.entries(profilesBySub).forEach(([userSub, profile]) => {
        const name =
            profile.displayName?.trim() ||
            `${profile.givenName || profile.profile?.givenName || ''} ${profile.familyName || profile.profile?.familyName || ''}`.trim() ||
            profile.email;
        if (name) nameBySub.set(userSub, name);
    });

    assignees.forEach(a => {
        if (!nameBySub.has(a.userSub)) {
            const name = `${a.givenName || ''} ${a.familyName || ''}`.trim() || a.email;
            if (name) nameBySub.set(a.userSub, name);
        }
    });

    organizationMembers.forEach(member => {
        if (!nameBySub.has(member.userSub)) {
            const name = `${member.givenName || ''} ${member.familyName || ''}`.trim() || member.email;
            if (name) nameBySub.set(member.userSub, name);
        }
    });

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (!rows.length) {
        return <p className="paragraph-3 project-overview-tab__empty">{t('projects.assigneeAssignmentHistoryEmpty')}</p>;
    }

    return (
        <div className="project-assignee-rate-history">
            <Table className="project-assignee-rate-history__table">
                <TableRow>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeRateHistoryAssignee')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeAssignmentHistoryStart')}</TableHeadCell>
                    <TableHeadCell className="project-assignee-rate-history__head">{t('projects.assigneeAssignmentHistoryEnd')}</TableHeadCell>
                </TableRow>
                {rows.map(row => {
                    const displayName = nameBySub.get(row.userSub) || t('projects.assigneeRateHistoryUnknownAssignee');
                    const isProfileLinkEnabled = organizationMemberSubs.has(row.userSub);
                    return (
                        <TableRow key={row.id}>
                            <TableCell>
                                {isProfileLinkEnabled ? (
                                    <Link className="paragraph-3 project-assignee-rate-history__assignee-link" to={`/profile/${row.userSub}`}>
                                        {displayName}
                                    </Link>
                                ) : (
                                    <span className="paragraph-3 project-assignee-rate-history__assignee-link project-assignee-rate-history__assignee-link--detached">
                                        {displayName}
                                    </span>
                                )}
                            </TableCell>
                            <TableCell>{formatToMonthDayYear(row.effectiveFrom)}</TableCell>
                            <TableCell>
                                {row.effectiveTo == null ? (
                                    <span className="paragraph-3 project-assignee-rate-history__active">
                                        {t('projects.assigneeRatePeriodActive')}
                                    </span>
                                ) : (
                                    formatToMonthDayYear(row.effectiveTo)
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </Table>
        </div>
    );
};
